/* Move-earlier / move-later for one row, and the hook that saves the result.
 *
 * Buttons rather than drag-and-drop, deliberately, and for the same reason the
 * gallery builder chose them: Expert Studio's main user is a crew member on a
 * phone, and the HTML5 drag API does not work on touch. Shipping drag would ship
 * a reorder the primary user cannot operate. Buttons work with mouse, touch and
 * keyboard for free.
 *
 * Every save sends the WHOLE list, matching worker/galleryorder.ts: order is one
 * fact about the set, not N facts about N rows. That is what lets the server
 * detect a stale caller — two people, one adds a photo while the other is
 * reordering — and refuse with the real current order instead of quietly
 * dropping the new one. A 409 therefore refetches rather than retrying.
 */
import { useMutation } from '@tanstack/react-query';
import { Icon } from '@/lib/icons';
import type { DragOrder } from './useDragOrder';

export function useMoveOrder(
  ids: readonly string[],
  save: (ids: string[]) => Promise<unknown>,
  opts: { onSaved: () => void; onError: (message: string) => void },
) {
  const m = useMutation({
    mutationFn: save,
    onSuccess: () => opts.onSaved(),
    onError: (e) => {
      opts.onError(e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : 'Could not save the new order.');
      // The server's refusal carries the real order; the only sane move is to
      // go and read it rather than insist on a list we now know is stale.
      opts.onSaved();
    },
  });

  const move = (id: string, delta: -1 | 1) => {
    const next = [...ids];
    const from = next.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= next.length) return;
    next.splice(to, 0, ...next.splice(from, 1));
    m.mutate(next);
  };

  return { move, busy: m.isPending };
}

export function MoveButtons({ id, ids, order, label = 'photo' }: {
  id: string;
  ids: readonly string[];
  order: { move: (id: string, delta: -1 | 1) => void; busy: boolean };
  label?: string;
}) {
  const index = ids.indexOf(id);
  // One item has nothing to be ordered against; showing dead arrows implies a
  // capability that is not there.
  if (index < 0 || ids.length < 2) return null;

  return (
    <span className="es-reorder" role="group" aria-label={`Reorder this ${label}`}>
      <button type="button" className="es-reorder__btn" disabled={index === 0 || order.busy}
        title={`Move this ${label} earlier`} aria-label={`Move this ${label} earlier`}
        onClick={() => order.move(id, -1)}>
        <Icon name="chevron-left" size={13} />
      </button>
      <span className="es-reorder__pos" aria-hidden="true">{index + 1}</span>
      <button type="button" className="es-reorder__btn" disabled={index === ids.length - 1 || order.busy}
        title={`Move this ${label} later`} aria-label={`Move this ${label} later`}
        onClick={() => order.move(id, 1)}>
        <Icon name="chevron-right" size={13} />
      </button>
    </span>
  );
}

/* The grip for a list or grid that drags (useDragOrder), and the keyboard's
   way to do the same thing: focus it and use the arrow keys, which move one
   place through useMoveOrder. A drag cannot be reached from a keyboard however
   it is built, so the grip carries both rather than the row growing arrows. */
export function DragGrip({ id, drag, keys, label = 'photo', className = 'es-grip' }: {
  id: string;
  drag: DragOrder;
  keys: { move: (id: string, delta: -1 | 1) => void; busy: boolean };
  label?: string;
  className?: string;
}) {
  return (
    <span className={className} role="button" tabIndex={0}
      title="Drag to reorder" aria-label={`Reorder this ${label}: drag, or use the arrow keys`}
      {...drag.gripProps(id)}
      onKeyDown={(e) => {
        if (keys.busy || drag.busy) return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); keys.move(id, -1); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); keys.move(id, 1); }
      }}>
      <Icon name="grip" size={14} />
    </span>
  );
}
